// SubscribeFeedDialog — issue + list + revoke calendar feed tokens.
//
// Used from:
//   - CalendarHub header (default scope = mine)
//   - ProductionDetail action bar (default scope = production:<id>)
//   - Settings → Calendar Feed Subscriptions (default scope = mine)
//
// One dialog handles three flows: subscribe (issue), display new token + QR
// + copy buttons, list existing tokens with last-used + revoke.

import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import type { CalToken } from '../../types/domain'
import { QRCodeSVG } from 'qrcode.react'
import { useLanguage } from '../../i18n/index.jsx'
import { Modal } from '../ui/Modal.jsx'
import {
  useCalTokens,
  useIssueCalToken,
  useRevokeCalToken,
} from '../../hooks/useCalTokens.js'

const DEFAULT_SCOPE = { scope_kind: 'mine', scope_id: null }

// Outer wrapper — only mounts the inner (hook-using) body when open. This
// keeps the dialog cheap when closed AND lets pages embed it without a
// QueryClientProvider in scope (smoke tests for CalendarHub etc. rely on
// hook-mocking, not on a wrapping provider).

interface ScopeRef {
  scope_kind: string;
  scope_id?: number | string | null;
  label?: string | null;
  last_used_at?: string | null;
}

interface IssuedToken {
  webcal_url: string;
  feed_url: string;
  google_url: string;
}

interface SubscribeFeedDialogProps {
  open: boolean;
  onClose: () => void;
  defaultScope?: ScopeRef;
  productionName?: string | null;
  venueName?: string | null;
}

export default function SubscribeFeedDialog(props: SubscribeFeedDialogProps) {
  if (!props.open) return null
  return <SubscribeFeedDialogBody {...props} />
}

function SubscribeFeedDialogBody({
  open,
  onClose,
  defaultScope = DEFAULT_SCOPE,
  productionName,
  venueName,
}: SubscribeFeedDialogProps) {
  const { t } = useLanguage()
  const tokensQuery = useCalTokens()
  const issue = useIssueCalToken()
  const revoke = useRevokeCalToken()
  const [label, setLabel] = useState('')
  const [newToken, setNewToken] = useState<IssuedToken | null>(null)
  const [issueError, setIssueError] = useState<string | null>(null)

  // Reset form whenever dialog is closed so re-opening starts clean.
  useEffect(() => {
    if (!open) {
      setLabel('')
      setNewToken(null)
      setIssueError(null)
    }
  }, [open])

  function scopeLabel(scope: ScopeRef) {
    if (scope.scope_kind === 'mine') return t('calFeed.scopeMine')
    if (scope.scope_kind === 'production') return productionName || t('calFeed.scopeProduction')
    if (scope.scope_kind === 'venue') return venueName || t('calFeed.scopeVenue')
    if (scope.scope_kind === 'all') return t('calFeed.scopeAll')
    return scope.scope_kind
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setIssueError(null)
    if (!label.trim()) {
      setIssueError(t('calFeed.errorLabelRequired'))
      return
    }
    try {
      const tok = await issue.mutateAsync({
        label: label.trim(),
        scope_kind: defaultScope.scope_kind,
        scope_id: defaultScope.scope_id,
      })
      setNewToken(tok as unknown as IssuedToken)
      setLabel('')
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string }
      const msg = e?.response?.data?.error || e?.message || ''
      if (/maximum tokens/i.test(msg)) setIssueError(t('calFeed.errorMaxReached'))
      else setIssueError(msg || t('calFeed.errorGeneric'))
    }
  }

  function copy(text: string) {
    if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {})
  }

  const existing: CalToken[] = tokensQuery.data || []

  return (
    <Modal open={open} onClose={onClose} title={t('calFeed.dialogTitle')} size="md">
      <div style={{ padding: 'var(--space-4)', display: 'grid', gap: 'var(--space-4)' }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          {t('calFeed.scopeLabel')}: <strong style={{ color: 'var(--text-primary)' }}>{scopeLabel(defaultScope)}</strong>
        </p>

        {!newToken && (
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <label htmlFor="cal-feed-label" style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              {t('calFeed.labelField')}
            </label>
            <input
              id="cal-feed-label"
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder={t('calFeed.labelPlaceholder')}
              maxLength={60}
              required
              autoFocus
              style={{
                background: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md, 6px)',
                padding: '8px 12px',
              }}
            />
            {issueError && (
              <p role="alert" style={{ color: 'var(--danger)', fontSize: '0.875rem', margin: 0 }}>
                {issueError}
              </p>
            )}
            <button
              type="submit"
              disabled={issue.isPending || !label.trim()}
              style={{
                background: 'var(--accent)',
                color: 'var(--text-inverse)',
                border: 0,
                borderRadius: 'var(--radius-md, 6px)',
                padding: '8px 16px',
                cursor: issue.isPending ? 'wait' : 'pointer',
                fontWeight: 500,
                justifySelf: 'start',
              }}
            >
              {issue.isPending ? t('loading') : t('calFeed.subscribe')}
            </button>
          </form>
        )}

        {newToken && (
          <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
            <p style={{ margin: 0, color: 'var(--success)' }}>{t('calFeed.success')}</p>

            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              <label style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{t('calFeed.webcalUrl')}</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  readOnly
                  value={newToken.webcal_url}
                  onFocus={e => e.target.select()}
                  style={{ flex: 1, background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md, 6px)', padding: '8px 12px', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.875rem' }}
                />
                <button
                  type="button"
                  onClick={() => copy(newToken.webcal_url)}
                  style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md, 6px)', padding: '8px 12px', cursor: 'pointer' }}
                >
                  {t('calFeed.copyWebcal')}
                </button>
              </div>

              <label style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{t('calFeed.httpsUrl')}</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  readOnly
                  value={newToken.feed_url}
                  onFocus={e => e.target.select()}
                  style={{ flex: 1, background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md, 6px)', padding: '8px 12px', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.875rem' }}
                />
                <button
                  type="button"
                  onClick={() => copy(newToken.feed_url)}
                  style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md, 6px)', padding: '8px 12px', cursor: 'pointer' }}
                >
                  {t('calFeed.copyHttps')}
                </button>
              </div>

              <a
                href={newToken.google_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--accent)', textDecoration: 'none', justifySelf: 'start' }}
              >
                {t('calFeed.addToGoogle')} →
              </a>
            </div>

            <div data-testid="subscribe-qr" style={{ display: 'grid', justifyItems: 'center', gap: 'var(--space-2)' }}>
              <div style={{ background: 'white', padding: '12px', borderRadius: 'var(--radius-md, 6px)' }}>
                <QRCodeSVG value={newToken.webcal_url} size={160} />
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 0 }}>{t('calFeed.qrHint')}</p>
            </div>
          </div>
        )}

        <hr style={{ border: 0, borderTop: '1px solid var(--border-default)' }} />

        <div>
          <h3 style={{ margin: '0 0 var(--space-2) 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
            {t('calFeed.existingTitle')}
          </h3>
          {tokensQuery.isLoading && <p style={{ color: 'var(--text-muted)' }}>{t('loading')}</p>}
          {!tokensQuery.isLoading && existing.length === 0 && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 0 }}>{t('calFeed.existingEmpty')}</p>
          )}
          {existing.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
              {existing.map(tk => (
                <li
                  key={tk.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: '8px 12px',
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-md, 6px)',
                  }}
                >
                  <div style={{ flex: 1, display: 'grid' }}>
                    <span style={{ color: 'var(--text-primary)' }}>{tk.label}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                      {scopeLabel(tk as unknown as ScopeRef)}
                      {String(tk.last_used_at ?? '') && ` · ${t('calFeed.lastUsed')} ${new Date(tk.last_used_at as string).toLocaleDateString()}`}
                    </span>
                  </div>
                  <button
                    type="button"
                    aria-label={t('calFeed.revokeAria', { label: tk.label })}
                    onClick={() => revoke.mutate(tk.id)}
                    disabled={revoke.isPending}
                    style={{
                      background: 'transparent',
                      color: 'var(--danger)',
                      border: '1px solid var(--danger)',
                      borderRadius: 'var(--radius-md, 6px)',
                      padding: '4px 12px',
                      cursor: revoke.isPending ? 'wait' : 'pointer',
                      fontSize: '0.875rem',
                    }}
                  >
                    {t('calFeed.revoke')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  )
}
